import { useEffect, useState } from 'react';
/**
 * Tracks the *visual* viewport — the part of the screen actually visible once the
 * software keyboard (or a pinch-zoom) is taken into account.
 *
 * Every mobile engine (Chrome, Firefox, Safari) defaults to `interactive-widget=
 * resizes-visual`: the keyboard shrinks the visual viewport but leaves the layout
 * viewport alone. `100dvh` and `position: fixed; inset: 0` still span the whole
 * screen, and the browser reveals the focused input by panning the visible area
 * inside that unchanged layout viewport — a full-screen fixed panel then runs under
 * the keyboard and, once panned, appears shifted off screen.
 *
 * `window.visualViewport` reports both the shrunken size and that pan offset, so a
 * fixed element can be pinned to what the user actually sees.
 *
 * Returns `null` while disabled, on the server, and on browsers without the API
 * (callers then keep their static CSS box).
 */
export function useVisualViewportRect(enabled) {
    const [rect, setRect] = useState(null);
    useEffect(() => {
        if (!enabled) {
            setRect(null);
            return;
        }
        const viewport = typeof window === 'undefined' ? undefined : window.visualViewport;
        if (!viewport)
            return;
        let frame = 0;
        const sync = () => {
            // The keyboard animates: resize/scroll fire in bursts, one rAF-coalesced
            // state update per frame is enough.
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                setRect({
                    height: viewport.height,
                    offsetLeft: viewport.offsetLeft,
                    offsetTop: viewport.offsetTop,
                    width: viewport.width,
                });
            });
        };
        sync();
        viewport.addEventListener('resize', sync);
        viewport.addEventListener('scroll', sync);
        return () => {
            cancelAnimationFrame(frame);
            viewport.removeEventListener('resize', sync);
            viewport.removeEventListener('scroll', sync);
        };
    }, [enabled]);
    return rect;
}
